import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { importNotion, previewNotion, NotionItem } from '../../api/notion';
import { ApiError } from '../../api/client';
import { Button } from '../../shared/components/Button';
import { Input } from '../../shared/components/Field';
import { Alert } from '../../shared/components/feedback';
import { Modal } from '../../shared/components/Modal';

type Row = NotionItem & { checked: boolean };

function scheduledTime(row: NotionItem): number {
  const time = row.scheduledAt ? Date.parse(row.scheduledAt + '+09:00') : NaN;
  return Number.isFinite(time) ? time : Infinity;
}

export function notionRowIssue(row: NotionItem): string | null {
  if (row.imported) return '이 계정에 이미 가져온 페이지입니다.';
  if (row.blockedReason) return row.blockedReason;
  if (!row.draftPost.trim() || row.draftPost.trim().length > 500) return '본문을 1~500자로 수정하세요.';
  if (!row.scheduledAt || !Number.isFinite(Date.parse(row.scheduledAt + '+09:00'))
      || Date.parse(row.scheduledAt + '+09:00') <= Date.now()) return '게시 예정일을 미래 시각으로 지정하세요.';
  return null;
}

export function NotionImportModal({ accountId, accountLabel, autoPublishEnabled, reviewRequired, onImported, onClose }: {
  accountId: number; accountLabel: string; autoPublishEnabled: boolean; reviewRequired: boolean;
  onImported: () => void; onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [defaultTime, setDefaultTime] = useState('09:00');
  const [rows, setRows] = useState<Row[]>([]);
  const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importProgress, setImportProgress] = useState<{ completed: number; total: number } | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mounted = useRef(true);
  const stopLoading = useRef(false);
  const fetching = useRef(false);
  const submitting = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const clearPreview = () => { setRows([]); setCursor(null); setLoaded(false); setSuccess(''); setError(''); };
  const resetPreview = () => { clearPreview(); setSources([]); setSourceId(''); };
  const message = (e: unknown) => e instanceof ApiError
    ? (e.code === 0 ? e.message : e.description || e.message)
    : '노션 요청에 실패했습니다. 다시 시도하세요.';

  async function load(nextSource = sourceId, more = false) {
    if (fetching.current || submitting.current) return;
    fetching.current = true;
    stopLoading.current = false;
    setLoading(true);
    setBusy(true); setError(''); setSuccess('');
    // Keep reviewed rows until the first successful response, so a failed refresh loses no edits.
    let replaceRows = !more;
    let nextCursor = more ? cursor : null;
    let resolvedSource = nextSource;
    const visited = new Set<string>();
    try {
      do {
        if (nextCursor) {
          if (visited.has(nextCursor)) throw new Error('Repeated Notion cursor');
          visited.add(nextCursor);
        }
        const result = await previewNotion(accountId, { url: url.trim(), defaultTime,
          dataSourceId: resolvedSource || undefined, cursor: nextCursor || undefined });
        if (!mounted.current) return;
        const replace = replaceRows;
        setRows((previous) => {
          const existing = replace ? [] : previous;
          const ids = new Set(existing.map((row) => row.pageId));
          const additions = result.items.filter((row) => {
            if (ids.has(row.pageId)) return false;
            ids.add(row.pageId); return true;
          }).map((row) => ({ ...row, checked: false }));
          return [...existing, ...additions];
        });
        replaceRows = false;
        if (result.dataSources.length) setSources(result.dataSources);
        resolvedSource = result.dataSourceId || resolvedSource;
        setSourceId(resolvedSource || '');
        nextCursor = result.nextCursor;
        setCursor(nextCursor); setLoaded(true);
      } while (nextCursor && !stopLoading.current);
    } catch (e) { if (mounted.current) setError(message(e)); }
    finally { fetching.current = false; if (mounted.current) { setBusy(false); setLoading(false); } }
  }

  function change(id: string, patch: Partial<Row>) {
    setRows((previous) => previous.map((row) => {
      if (row.pageId !== id) return row;
      const updated = { ...row, ...patch };
      return { ...updated, checked: notionRowIssue(updated) ? false : updated.checked };
    }));
  }

  const sortedRows = useMemo(() => [...rows].sort((a, b) => {
    const first = scheduledTime(a), second = scheduledTime(b);
    return first < second ? -1 : first > second ? 1 : 0;
  }), [rows]);
  const selected = sortedRows.filter((row) => row.checked);
  async function submit() {
    if (submitting.current || fetching.current) return;
    if (!selected.length || selected.some(notionRowIssue)) {
      setError('본문과 예약 시각이 유효한 항목을 선택하세요.'); return;
    }
    submitting.current = true;
    setBusy(true); setError(''); setSuccess('');
    setImportProgress({ completed: 0, total: selected.length });
    let completed = 0, created = 0, skipped = 0, awaitingApproval = 0;
    try {
      // Keep requests within the server's validation/transaction limit while allowing any selection size.
      for (let offset = 0; offset < selected.length; offset += 20) {
        if (!mounted.current) return;
        const batch = selected.slice(offset, offset + 20);
        const result = await importNotion(accountId, batch.map((row) => ({
          pageId: row.pageId, lastEditedTime: row.lastEditedTime,
          draftPost: row.draftPost.trim(), scheduledAt: row.scheduledAt!,
        })));
        if (!mounted.current) return;
        completed += batch.length;
        created += result.reservationIds.length; skipped += result.skipped; awaitingApproval += result.awaitingApproval;
        const importedIds = new Set(batch.map((row) => row.pageId));
        setRows((previous) => previous.map((row) => importedIds.has(row.pageId) ? { ...row, imported: true, checked: false } : row));
        setImportProgress({ completed, total: selected.length });
        setSuccess('예약 ' + created + '건 생성 · 중복 제외 ' + skipped
          + '건' + (awaitingApproval ? ' · 승인 대기 ' + awaitingApproval + '건' : ''));
      }
    } catch (e) {
      if (mounted.current) setError(message(e) + ' 완료된 항목은 유지됩니다. 남은 선택 항목을 확인한 뒤 다시 예약하세요.');
    } finally {
      submitting.current = false;
      if (mounted.current) {
        setBusy(false); setImportProgress(null);
        if (completed) onImported();
      }
    }
  }

  return <Modal open onClose={busy ? undefined : onClose} width={850} labelledBy="notion-import-title">
    <Panel>
      <Header>
        <h2 id="notion-import-title">노션에서 예약 가져오기</h2>
        <Help>콘텐츠를 불러오고, 본문과 시각을 확인한 뒤 예약하세요.</Help>
        <AccountBadge>발행 계정: {accountLabel}</AccountBadge>
      </Header>
      <Help>Hook → 본문 초안 → CTA를 원문으로 저장합니다. 발행 직전에 AI가 계정의 말투 설정으로 재작성하며 AI 사용량이 차감됩니다.</Help>
      {!autoPublishEnabled && <Alert tone="warning">현재 노션 자동 발행이 OFF입니다. 가져온 뒤 보드에서 노션 자동 발행을 켜야 예약 시각에 발송됩니다.</Alert>}
      {reviewRequired && <Alert tone="info">이 계정은 발행 전 승인이 필요합니다. 예약 시각에 AI가 만든 최종 글을 보드에서 확인하고 승인하세요.</Alert>}
      <form onSubmit={(e) => { e.preventDefault(); void load(); }}>
        <Label>노션 페이지 또는 데이터베이스 링크
          <Input type="url" required value={url} disabled={busy} placeholder="https://www.notion.so/..."
            onChange={(e) => { setUrl(e.target.value); resetPreview(); }} />
        </Label>
        <RowLayout>
          <Label>날짜만 있는 항목의 기본 시각 (한국 시간)
            <Input type="time" required value={defaultTime} disabled={busy}
              onChange={(e) => { setDefaultTime(e.target.value); clearPreview(); }} />
          </Label>
          <Button type="submit" disabled={busy || !url.trim() || !defaultTime}>{loading ? `불러오는 중… ${rows.length}개` : '전체 불러오기'}</Button>
          {loading && <Button type="button" $variant="secondary" onClick={() => { stopLoading.current = true; }}>불러오기 중단</Button>}
        </RowLayout>
      </form>
      <Help>상위 페이지 링크를 넣으면 안에 있는 표를 찾습니다. 같은 표의 여러 보기는 한 번만 가져옵니다.</Help>
      <details>
        <summary>노션 연결 방법과 읽기 권한 확인</summary>
        <Help>노션 페이지의 ••• → 연결에서 이 서비스에 연결한 통합을 추가하고 읽기 권한을 허용하세요. 연결된 보기라면 원본 데이터베이스에도 권한이 필요합니다. 웹에 공개한 링크만으로는 연결되지 않습니다.</Help>
        <Help>노션에서 원문을 수정했다면 다시 불러오세요. 성공적으로 다시 불러오면 여기서 검토한 본문과 선택은 초기화됩니다.</Help>
      </details>
      {sources.length > 1 && <Label>가져올 표
        <select value={sourceId} disabled={busy} onChange={(e) => {
          const id = e.target.value;
          setSourceId(id); clearPreview();
          if (id) void load(id);
        }}>
          <option value="">표를 선택하세요</option>
          {sources.map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}
        </select>
      </Label>}
      {sources.length === 1 && <Help>연결된 표: {sources[0].name}</Help>}
      {loaded && sources.length > 1 && !sourceId && <Alert tone="info">여러 표를 찾았습니다. 가져올 표를 선택하세요.</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {success && <Alert tone="success">{success}</Alert>}
      {loaded && !loading && !cursor && rows.length === 0 && (sourceId || sources.length <= 1) && <Help>가져올 항목이 없습니다.</Help>}
      {rows.length > 0 && <>
        <Help>게시 예정일이 빠른 순서입니다. 날짜가 없는 항목은 마지막에 표시됩니다.</Help>
        <RowLayout><strong aria-live="polite">불러온 항목 {rows.length}개 · 선택 {selected.length}개</strong>
          <SelectionActions>
          <Button $variant="ghost" $size="sm" disabled={busy} onClick={() => {
            setRows(rows.map((row) => ({ ...row, checked: !notionRowIssue(row) })));
          }}>예약 가능한 항목 전체 선택</Button>
          <Button type="button" $variant="ghost" $size="sm" disabled={busy || !selected.length}
            onClick={() => setRows((previous) => previous.map((row) => ({ ...row, checked: false })))}>선택 해제</Button>
          </SelectionActions>
        </RowLayout>
        {sortedRows.map((row) => {
          const issue = notionRowIssue(row);
          const blocked = row.imported || !!row.blockedReason;
          return <Entry key={row.pageId} $selected={row.checked}>
            <RowLayout>
              <SelectionLabel><input type="checkbox" aria-label={row.title + ' 선택'} checked={row.checked}
                disabled={busy || !!issue}
                onChange={(e) => change(row.pageId, { checked: e.target.checked })} /> <strong>{row.title}</strong></SelectionLabel>
              <a href={'https://www.notion.so/' + row.pageId.replace(/-/g, '')} target="_blank" rel="noreferrer">노션 원문 ↗</a>
            </RowLayout>
            <Help>노션 상태: {row.notionStatus || '미지정'}</Help>
            {blocked ? <Help>{issue}</Help> : <>
              <details><summary>원본 Hook · CTA · 본문 초안</summary><Original>Hook: {row.hook || '없음'}{'\n\n'}본문 초안: {row.body}{'\n\n'}CTA: {row.cta || '없음'}</Original></details>
              <Label>AI 제작에 사용할 원문 · {row.draftPost.trim().length}/500
                <TextArea aria-label={row.title + ' 발행 본문'} aria-invalid={!row.draftPost.trim() || row.draftPost.trim().length > 500} rows={5} value={row.draftPost} disabled={busy}
                  onChange={(e) => change(row.pageId, { draftPost: e.target.value })} />
              </Label>
              <Label>게시 예정일 (한국 시간)
                <Input aria-label={row.title + ' 게시 예정일'} type="datetime-local" value={row.scheduledAt?.slice(0, 16) || ''} disabled={busy}
                  onChange={(e) => change(row.pageId, { scheduledAt: e.target.value ? e.target.value + ':00' : null })} />
              </Label>
              {issue && <Alert tone="warning">{issue}</Alert>}
            </>}
          </Entry>;
        })}
      </>}
      {cursor && !loading && <Button $variant="secondary" disabled={busy} onClick={() => void load(sourceId, true)}>남은 항목 모두 불러오기</Button>}
      <Help>예약 시각 이후 기존 발행 간격·일일 한도에 따라 순차 발송됩니다. 가져온 뒤의 노션 수정과 상태는 자동 동기화되지 않습니다.</Help>
      <Footer>
        <Button $variant="secondary" disabled={busy} onClick={onClose}>닫기</Button>
        <Button disabled={busy || !selected.length || selected.some(notionRowIssue)} onClick={() => void submit()}>
          {importProgress ? `예약 등록 중… ${importProgress.completed}/${importProgress.total}건` : `${accountLabel}에 ${selected.length}건 예약`}
        </Button>
      </Footer>
    </Panel>
  </Modal>;
}

const Panel = styled.div`
  padding: 28px; display: grid; gap: 16px;
  h2 { margin: 0; font-size: 22px; letter-spacing: -0.03em; }
  select { width: 100%; min-height: 44px; padding: 10px 12px; border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 8px; background: ${({ theme }) => theme.colors.surface}; color: ${({ theme }) => theme.colors.textPrimary}; font: inherit; }
  summary { min-height: 44px; padding: 12px 0; cursor: pointer; font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary}; }
  @media (max-width: 600px) { padding: 20px 16px; h2 { font-size: 20px; } }
`;
const Header = styled.header`display: grid; gap: 4px; padding-bottom: 16px; border-bottom: 1px solid ${({ theme }) => theme.colors.border};`;
const AccountBadge = styled.p`margin: 4px 0 0; width: fit-content; padding: 7px 10px; border-radius: 8px; background: ${({ theme }) => theme.colors.primarySoft}; color: ${({ theme }) => theme.colors.primary}; font-size: 13px; font-weight: 600; overflow-wrap: anywhere;`;
const RowLayout = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; > strong { font-size: 14px; } > a { display: inline-flex; align-items: center; min-height: 44px; font-size: 13px; color: ${({ theme }) => theme.colors.primary}; }`;
const SelectionActions = styled.div`display: flex; flex-wrap: wrap; gap: 4px;`;
const SelectionLabel = styled.label`display: flex; align-items: center; gap: 10px; min-height: 44px; min-width: 0; cursor: pointer; font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; input { width: 18px; height: 18px; flex-shrink: 0; accent-color: ${({ theme }) => theme.colors.primary}; }`;
const Help = styled.p`margin: 4px 0; font-size: 13px; line-height: 1.7; color: ${({ theme }) => theme.colors.textSecondary};`;
const Label = styled.label`display: grid; gap: 8px; margin: 10px 0; font-size: 13px; line-height: 1.6; input { min-width: 0; }`;
const Entry = styled.article<{ $selected: boolean }>`
  min-width: 0; border: 1px solid ${({ theme, $selected }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 12px; padding: 18px; background: ${({ theme, $selected }) => $selected ? theme.colors.primarySoft : theme.colors.surface};
  @media (max-width: 600px) { padding: 14px; }
`;
const TextArea = styled.textarea`
  width: 100%; box-sizing: border-box; resize: vertical; padding: 12px 14px; font: inherit; font-size: 14px; line-height: 1.75;
  color: ${({ theme }) => theme.colors.textPrimary}; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 8px;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primarySoft}; }
  &[aria-invalid='true'] { border-color: ${({ theme }) => theme.colors.danger}; }
  &:disabled { opacity: 0.65; }
  @media (max-width: 600px) { font-size: 16px; }
`;
const Original = styled.p`white-space: pre-wrap; font-size: 13px; line-height: 1.7; overflow-wrap: anywhere; padding: 12px; border-radius: 8px; background: ${({ theme }) => theme.colors.surfaceMuted};`;
const Footer = styled.div`
  position: sticky; bottom: 0; z-index: 1; display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 28px; margin: 0 -28px -28px; border-top: 1px solid ${({ theme }) => theme.colors.border}; background: ${({ theme }) => theme.colors.surface};
  @media (max-width: 600px) { bottom: 0; padding: 12px 16px; margin: 0 -16px -20px; flex-wrap: wrap; button { flex: 1 1 auto; } }
`;
